import "./reviews.scss";
import { BRAND } from "constants/brand";
import { useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { NavLink, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { Ring } from "@uiball/loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import { Toast } from "primereact/toast";

import {
    useListAdminReviews,
    useRecomputeAllReviewAggregates,
    useSetReviewStatus,
} from "hooks/useReviews";
import { useGetVendorsDropdown } from "hooks/useVendors";
import { useGetUsersDropdown } from "hooks/useUser";
import { useGetProductsDropdown } from "hooks/useProducts";
import { formatDate } from "utils/dateFormat";

/**
 * Stars renderer for the table — 5 stars, filled up to `value`.
 */
const Stars = ({ value }) => {
    const filled = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
    return (
        <span aria-label={`${filled} من 5 نجوم`} style={{ color: "#f5a623" }}>
            {[1, 2, 3, 4, 5].map((i) => (
                <FontAwesomeIcon key={i} icon={i <= filled ? faStarSolid : faStarRegular} />
            ))}
        </span>
    );
};

const TARGET_TYPE_LABELS = {
    product: "منتج",
    vendor: "مطعم",
    delivery: "سائق",
};

/**
 * Build the cell label for the "target" column. Shows the actual name
 * (vendor name / product title / driver name) instead of just "مطعم
 * #218" so the moderator can scan the queue without opening each row.
 */
const targetLabel = (review) => {
    const type = TARGET_TYPE_LABELS[review.targetType] ?? review.targetType;
    const name = review.target?.name;
    if (name) {
        if (review.targetType === "product" && review.target?.vendorName) {
            return `${type}: ${name} (${review.target.vendorName})`;
        }
        return `${type}: ${name}`;
    }
    return `${type} #${review.targetId}`;
};

const STATUS_LABELS = {
    visible: "ظاهر",
    hidden: "مخفي",
};

/**
 * Admin reviews moderation page (`/reviews`). Lists all reviews with
 * filters (status, target type, rating range, free-text search) and
 * inline hide/unhide actions per row.
 *
 * Visible to admins with `manage_reviews` permission or super_admin.
 * Vendor-role users land on `/my-reviews` instead (see MyReviews.js).
 */
const Reviews = () => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const cities = useSelector((state) => state.city.cities);
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);
    const toast = useRef(null);

    const notify = (severity, detail) => {
        toast.current?.show({ severity, detail, life: 3000 });
    };

    // Dropdowns: vendors are always populated; products only when a
    // vendor is picked (the dropdown endpoint caps to vendor scope to
    // keep payload sane); delivery drivers via the role=delivery scope
    // of the users dropdown.
    const selectedVendorId = searchParams.get("vendorId") || "";
    const { vendors } = useGetVendorsDropdown();
    const { users: deliveryUsers } = useGetUsersDropdown({ role: "delivery" });
    const { products: vendorProducts } = useGetProductsDropdown(
        { vendorId: selectedVendorId },
        { enabled: Boolean(selectedVendorId) },
    );

    const params = {
        page,
        limit: 20,
    };
    if (searchParams.get("status")) params.status = searchParams.get("status");
    if (searchParams.get("targetType")) params.targetType = searchParams.get("targetType");
    if (searchParams.get("targetId")) params.targetId = searchParams.get("targetId");
    if (searchParams.get("vendorId")) params.vendorId = searchParams.get("vendorId");
    if (searchParams.get("vendorScope")) params.vendorScope = searchParams.get("vendorScope");
    if (searchParams.get("cityId")) params.cityId = searchParams.get("cityId");
    if (searchParams.get("minRating")) params.minRating = searchParams.get("minRating");
    if (searchParams.get("maxRating")) params.maxRating = searchParams.get("maxRating");
    if (searchParams.get("search")) params.search = searchParams.get("search");

    const { reviews, totalPages, totalCount, summary, loading } = useListAdminReviews(params);
    const setStatusMutation = useSetReviewStatus();
    const recomputeMutation = useRecomputeAllReviewAggregates();

    const setFilter = (key, value) => {
        if (value === "" || value === null || value === undefined) {
            searchParams.delete(key);
        } else {
            searchParams.set(key, String(value));
        }
        setSearchParams(searchParams);
        setPage(1);
    };

    /**
     * Vendor / delivery / product filters all map to (targetType,
     * targetId) on the API. They're mutually exclusive — picking one
     * clears the others. Vendor also carries vendorScope (self vs all).
     */
    const pickVendor = (vendorId) => {
        if (!vendorId) {
            searchParams.delete("vendorId");
            searchParams.delete("vendorScope");
            // Also clear product since it was scoped to this vendor.
            if (searchParams.get("targetType") === "product") {
                searchParams.delete("targetType");
                searchParams.delete("targetId");
            }
        } else {
            searchParams.set("vendorId", String(vendorId));
            // Default to "self" scope; explicit toggle below switches it.
            if (!searchParams.get("vendorScope")) {
                searchParams.set("vendorScope", "self");
            }
            // Clear conflicting per-target filter.
            if (searchParams.get("targetType") === "delivery") {
                searchParams.delete("targetType");
                searchParams.delete("targetId");
            }
        }
        setSearchParams(searchParams);
        setPage(1);
    };

    const pickDelivery = (userId) => {
        if (!userId) {
            if (searchParams.get("targetType") === "delivery") {
                searchParams.delete("targetType");
                searchParams.delete("targetId");
            }
        } else {
            searchParams.set("targetType", "delivery");
            searchParams.set("targetId", String(userId));
            // Mutually exclusive with vendor filter.
            searchParams.delete("vendorId");
            searchParams.delete("vendorScope");
        }
        setSearchParams(searchParams);
        setPage(1);
    };

    const pickProduct = (productId) => {
        if (!productId) {
            if (searchParams.get("targetType") === "product") {
                searchParams.delete("targetType");
                searchParams.delete("targetId");
            }
        } else {
            searchParams.set("targetType", "product");
            searchParams.set("targetId", String(productId));
        }
        setSearchParams(searchParams);
        setPage(1);
    };

    const toggleVendorScope = () => {
        const cur = searchParams.get("vendorScope") || "self";
        searchParams.set("vendorScope", cur === "self" ? "all" : "self");
        setSearchParams(searchParams);
        setPage(1);
    };

    const toggleStatus = async (review) => {
        const next = review.status === "visible" ? "hidden" : "visible";
        try {
            await setStatusMutation.mutateAsync({ id: review.id, status: next });
            notify("success", next === "hidden" ? "تم إخفاء التقييم" : "تم إظهار التقييم");
        } catch (err) {
            notify("error", err?.response?.data?.message || "فشل تحديث الحالة");
        }
    };

    const triggerRecompute = async () => {
        if (!window.confirm("هل أنت متأكد من إعادة احتساب جميع التقييمات؟")) return;
        try {
            const data = await recomputeMutation.mutateAsync();
            notify(
                "success",
                `تم إعادة الاحتساب — ${data.vendors} مطعم، ${data.products} منتج، ${data.deliveries} سائق`,
            );
        } catch (err) {
            notify("error", err?.response?.data?.message || "فشل إعادة الاحتساب");
        }
    };

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                }}
            >
                <h2 style={{ margin: 0 }}>التقييمات ({totalCount})</h2>
                {adminInfo?.super_admin ? (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={triggerRecompute}
                        disabled={recomputeMutation.isLoading}
                    >
                        {recomputeMutation.isLoading ? "..." : "إعادة احتساب التقييمات"}
                    </button>
                ) : null}
            </div>

            {/* Summary card — reflects whatever filter is active. Renders
                even with zero results so the moderator sees the empty
                state instead of a missing card. */}
            <div className="reviews-summary">
                <div>
                    <div className="reviews-summary__label">المتوسط</div>
                    <div className="reviews-summary__value reviews-summary__value--brand">
                        {summary?.averageRating !== null && summary?.averageRating !== undefined
                            ? `★ ${Number(summary.averageRating).toFixed(2)}`
                            : "—"}
                    </div>
                </div>
                <div>
                    <div className="reviews-summary__label">العدد</div>
                    <div className="reviews-summary__value">{summary?.count ?? totalCount}</div>
                </div>
                {summary?.target?.name ? (
                    <div>
                        <div className="reviews-summary__label">الهدف</div>
                        <div className="reviews-summary__target">{summary.target.name}</div>
                    </div>
                ) : null}
                {summary?.byRating ? (
                    <div className="reviews-summary__breakdown">
                        {[5, 4, 3, 2, 1].map((star) => (
                            <div
                                key={star}
                                className="reviews-summary__bucket"
                                title={`${summary.byRating[star] ?? 0} تقييمات بـ ${star} نجوم`}
                            >
                                <span className="reviews-summary__star">
                                    <FontAwesomeIcon icon={faStarSolid} />
                                </span>
                                <strong>{star}</strong>
                                <span className="reviews-summary__count">
                                    ({summary.byRating[star] ?? 0})
                                </span>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>

            <div
                className="row row-cols-2 row-cols-md-3 filters"
                style={{ padding: "0 20px 20px", alignItems: "end" }}
            >
                <div className="col">
                    <h6>المدينة</h6>
                    <Form.Select
                        value={searchParams.get("cityId") || ""}
                        onChange={(e) => setFilter("cityId", e.target.value)}
                    >
                        <option value="">الكل</option>
                        {(cities ?? []).map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>

                <div className="col">
                    <h6>المطعم</h6>
                    <Form.Select
                        value={searchParams.get("vendorId") || ""}
                        onChange={(e) => pickVendor(e.target.value)}
                    >
                        <option value="">الكل</option>
                        {(vendors ?? []).map((v) => (
                            <option key={v.id} value={v.id}>
                                {v.name}
                            </option>
                        ))}
                    </Form.Select>
                    {searchParams.get("vendorId") ? (
                        <Form.Check
                            type="checkbox"
                            id="vendor-scope-all"
                            label="شمل المنتجات والكباتن"
                            checked={searchParams.get("vendorScope") === "all"}
                            onChange={toggleVendorScope}
                            style={{ marginTop: "4px", fontSize: "13px" }}
                        />
                    ) : null}
                </div>

                <div className="col">
                    <h6>السائق</h6>
                    <Form.Select
                        value={
                            searchParams.get("targetType") === "delivery"
                                ? searchParams.get("targetId") || ""
                                : ""
                        }
                        onChange={(e) => pickDelivery(e.target.value)}
                    >
                        <option value="">الكل</option>
                        {(deliveryUsers ?? []).map((u) => (
                            <option key={u.id} value={u.id}>
                                {u.name || `#${u.id}`}
                            </option>
                        ))}
                    </Form.Select>
                </div>

                <div className="col">
                    <h6>المنتج</h6>
                    <Form.Select
                        value={
                            searchParams.get("targetType") === "product"
                                ? searchParams.get("targetId") || ""
                                : ""
                        }
                        onChange={(e) => pickProduct(e.target.value)}
                        disabled={!selectedVendorId}
                    >
                        <option value="">{selectedVendorId ? "الكل" : "اختر المطعم أولًا"}</option>
                        {(vendorProducts ?? []).map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.title}
                            </option>
                        ))}
                    </Form.Select>
                </div>

                <div className="col">
                    <h6>النوع</h6>
                    <Form.Select
                        value={searchParams.get("targetType") || ""}
                        onChange={(e) => {
                            // Setting targetType from this generic dropdown
                            // also clears targetId — it's a broad filter,
                            // not a target picker.
                            if (!e.target.value) {
                                searchParams.delete("targetType");
                                searchParams.delete("targetId");
                            } else {
                                searchParams.set("targetType", e.target.value);
                                searchParams.delete("targetId");
                            }
                            setSearchParams(searchParams);
                            setPage(1);
                        }}
                    >
                        <option value="">الكل</option>
                        <option value="product">منتج</option>
                        <option value="vendor">مطعم</option>
                        <option value="delivery">السائق</option>
                    </Form.Select>
                </div>

                <div className="col">
                    <h6>الحالة</h6>
                    <Form.Select
                        value={searchParams.get("status") || ""}
                        onChange={(e) => setFilter("status", e.target.value)}
                    >
                        <option value="">الكل</option>
                        <option value="visible">ظاهر</option>
                        <option value="hidden">مخفي</option>
                    </Form.Select>
                </div>

                <div className="col">
                    <h6>النجوم من / إلى</h6>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Form.Select
                            value={searchParams.get("minRating") || ""}
                            onChange={(e) => setFilter("minRating", e.target.value)}
                        >
                            <option value="">من</option>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Select
                            value={searchParams.get("maxRating") || ""}
                            onChange={(e) => setFilter("maxRating", e.target.value)}
                        >
                            <option value="">إلى</option>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                </div>

                <div className="col">
                    <h6>بحث في التعليقات</h6>
                    <SearchInput
                        initialValue={searchParams.get("search") || ""}
                        onSearch={(value) => setFilter("search", value)}
                        placeholder="بحث في التعليق أو رقم التقييم"
                        minWidth={300}
                    />
                </div>
            </div>

            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div style={{ overflowX: "auto", padding: "0 20px" }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>التاريخ</th>
                                    <th>النجوم</th>
                                    <th>الهدف</th>
                                    <th>المُقَيِّم</th>
                                    <th>التعليق</th>
                                    <th>الحالة</th>
                                    <th>الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.id}</td>
                                        <td>{formatDate(r.createdAt)}</td>
                                        <td>
                                            <Stars value={r.rating} />
                                        </td>
                                        <td>{targetLabel(r)}</td>
                                        <td>{r.user?.name ?? `#${r.userId}`}</td>
                                        <td
                                            style={{
                                                maxWidth: "300px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                            title={r.comment ?? ""}
                                        >
                                            {r.comment || "—"}
                                        </td>
                                        <td>
                                            <span
                                                style={{
                                                    color:
                                                        r.status === "hidden" ? "#cc0000" : BRAND,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {STATUS_LABELS[r.status]}
                                            </span>
                                        </td>
                                        <td>
                                            <NavLink
                                                to={`/reviews/${r.id}`}
                                                className="btn btn-sm btn-outline-primary"
                                                style={{ marginLeft: "4px" }}
                                            >
                                                تفاصيل
                                            </NavLink>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${
                                                    r.status === "visible"
                                                        ? "btn-outline-danger"
                                                        : "btn-outline-success"
                                                }`}
                                                onClick={() => toggleStatus(r)}
                                                disabled={setStatusMutation.isLoading}
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        r.status === "visible" ? faEyeSlash : faEye
                                                    }
                                                />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {reviews.length === 0 ? (
                            <div style={{ padding: "40px", textAlign: "center", color: "#777" }}>
                                لا توجد تقييمات تطابق هذه الفلاتر
                            </div>
                        ) : null}
                    </div>

                    {totalPages > 1 ? (
                        <Stack
                            spacing={2}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: "20px",
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                            />
                        </Stack>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default Reviews;
